import React, { useState, useCallback } from "react";
import TopNav from "../layouts/TopNav";
import "../Styles/loading.css";
import { CircularProgress } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import ReLoginModal from "../components/ReLoginModal";
import api from "../util/axios";

interface LicenseFields {
  licenseId: string;
  licenseOwner: string;
  destination: string;
  truckNumber: string;
}

interface Payload {
  type: string;
  items: {
    _id: string;
    quantityToLoad: number;
    approvalNumber: string | undefined;
  }[];
  truckNumber: string;
  customer: string;
  licenseId?: string; 
}

const MergedLoadingSlip = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [reLogin, setReLogin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadAll, setLoadAll] = useState(false);
  const [isLicenseModalOpen, setIsLicenseModalOpen] = useState(false);
  const [licenseNumberInput, setLicenseNumberInput] = useState<string>('');
  const [fields, setFields] = useState<LicenseFields>({
    licenseId: '',
    licenseOwner: '',
    destination: '',
    truckNumber: '',
  });

  const mergedData = location.state; // Contains mergedRequisition from LoadingSlipTable
  const requisitions = mergedData?.mergedIds || [];
  const items = mergedData?.items || [];
  const token = window.localStorage.getItem("token");
  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };

  // State for form inputs
  const [formData, setFormData] = useState({
    truckNumber: mergedData?.truckNumber || "",
    quantities: items.map((item: any) => ({
      itemId: item._id,
      quantityToLoad: "",
      availableQuantity: parseInt(item.volume, 10) || 0,
    })),

    customer: mergedData?.customer?.businessName || "",
  });

  // Handle changes to truckNumber
  const handleTruckNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      truckNumber: e.target.value,
    }));
  };

  // Handle changes to quantity inputs
  const handleQuantityChange = (index: number, value: string) => {
    const newQuantities = [...formData.quantities];
    newQuantities[index] = {
      ...newQuantities[index],
      quantityToLoad: value,
    };
    setFormData((prev) => ({
      ...prev,
      quantities: newQuantities,
    }));
  };

  const handleLoadAllChange = () => {
    setLoadAll(!loadAll);
    if (!loadAll) {
      setFormData((prev) => ({
        ...prev,
        quantities: prev.quantities.map((q: any) => ({
          ...q,
          quantityToLoad: q.availableQuantity.toString(),
        })),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        quantities: prev.quantities.map((q: any) => ({
          ...q,
          quantityToLoad: "",
        })),
      }));
    }
  };

  const validateQuantities = () => {
    for (const { quantityToLoad, availableQuantity } of formData.quantities) {
      const quantity = parseInt(quantityToLoad, 10);
      if (isNaN(quantity) || quantity === 0) {
        Swal.fire({
          title: "Error!",
          text: "Quantity to load cannot be zero or empty.",
          icon: "error",
          confirmButtonColor: "red",
          confirmButtonText: "Try Again",
        });
        return false;
      }
      if (quantity > availableQuantity) {
        Swal.fire({
          title: "Error!",
          text: "Quantity to load cannot exceed available quantity.",
          icon: "error",
          confirmButtonColor: "red",
          confirmButtonText: "Try Again",
        });
        return false;
      }
    }
    return true;
  };

  const handleSelectLicense = useCallback(
    (licenseData: {
      _id: string;
      licenseNumber: string;
      licenseOwner: string;
      destination: string;
      documentUrl?: string;
      programId?: string;
      truckNumber?: string;
      quantity?: string;
      product?: { id: string; name: string; selling: number };
      exDepotPrice?: string;
      issuedDate?: string;
      expiryDate?: string;
    }) => {
      setFields({
        licenseId: licenseData._id, // Store license._id
        licenseOwner: licenseData.licenseOwner,
        destination: licenseData.destination,
        truckNumber: licenseData.truckNumber || '',
      });
      setLicenseNumberInput(licenseData.licenseNumber); // Display licenseNumber
      // setSelectedCustomer({ _id: licenseData.customerId, businessName: licenseData.businessName });
      setIsLicenseModalOpen(false);
    },
    []
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateQuantities()) {
      return;
    }

    setLoading(true);

    const items = formData.quantities.map((quantity: any, index: number) => ({
      _id: quantity.itemId,
      quantityToLoad: parseInt(quantity.quantityToLoad, 10),
      approvalNumber: mergedData.selectedData[index]?.approvalNumber,
    }));

    const payload: Payload = {
      type: "multiple",
      items: items,
      truckNumber: formData.truckNumber,
      customer: formData.customer,
    };
     if (fields.licenseId) {
      payload.licenseId = fields.licenseId;
    } 

    const licenseData = fields;

    api
      .post("/loading-slip", payload, { headers })
      .then((res) => {
        setLoading(false);
        Swal.fire({
          title: "Success!",
          text: `${res.data.message}`,
          icon: "success",
          confirmButtonColor: "green",
          confirmButtonText: "Continue",
        });
        // console.log("Sent payload");
        navigate("/dashboard/slip", { state: { originalData: mergedData, newData: res.data.data, licenseData: licenseData } });
      })
      .catch((err) => {
        console.error(err);
        Swal.fire({
          title: "Error!",
          text: `${err.response.data.message}`,
          icon: "error",
          confirmButtonColor: "red",
          confirmButtonText: "Try Again",
        });
        if (err.response.data.message === "jwt expired" || err.response.data.message === "jwt malformed") {
           
          setReLogin(true);
        }
        setLoading(false);
      });
  };

  return (
    <div className="board">
      {reLogin ? <ReLoginModal /> : null}
      <TopNav title="Merged Loading Slip" />

      <div className="loadingSlip">
        <h2>Generate Merged Loading Slip</h2>
        <p>Slip based on Multiple Approvals</p>
        <div className="generateSlip flex">
          <form onSubmit={handleSubmit} className="singleSlip">

            {items.map((item: any, index: number) => (
              <div key={item._id} className="input lsInput">
                <label>
                  Amount to load for {item.name} (Available Quantity: {formData.quantities[index].availableQuantity})
                </label>
                <input
                  type="number"
                  value={formData.quantities[index].quantityToLoad}
                  onChange={(e) => handleQuantityChange(index, e.target.value)}
                  disabled={loadAll}
                />
              </div>
            ))}

            <div style={{ fontSize: "16px", marginBottom: "1em" }}>
              <input type="checkbox" checked={loadAll} onChange={handleLoadAllChange} />
              <span>Click to load all quantities</span>
            </div>

            <div className="slipBr">
              <div className="input lsInput">
                <label>Truck Number</label>
                <input
                  type="text"
                  name="truckNumber"
                  value={formData.truckNumber}
                  onChange={handleTruckNumberChange}
                />
              </div>
            </div>

            <div className="input lsInput">
              <label>Customer Name</label>
              <input type="text" value={formData.customer} readOnly />
            </div>

            <div className="input lsInput">
              <label>Approval Numbers</label>
              <input type="text" value={requisitions.join(", ")} readOnly />
            </div>

            <button type="submit" className="catBtn11">
              {loading ? (
                <CircularProgress
                  style={{
                    color: "#fff",
                    backgroundColor: "transparent",
                    width: "20px",
                    height: "20px",
                  }}
                />
              ) : (
                "Generate Merged Slip"
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MergedLoadingSlip;